import React, { useState } from "react";
import "./CriticalActivityList.css";
import "../AdapidActivityList/AdapidActivityList.css";
import { 
  AlertTriangle, 
  Shield, 
  Clock, 
  Activity, 
  Globe, 
  Mail, 
  Check, 
  Loader2 
} from "lucide-react";
import { itdrApi } from "../../../../services/api";
import { removeCriticalIncident } from "../../../../store/slices/authActivitySlice";
import { useDispatch } from "react-redux";
import { useTimezone } from "../../../../context/TimezoneContext";
import { formatDateByTimezone } from "../../../../utils/dateFormatter";

export const CriticalActivityList = ({ events = [], onRefresh }) => {
  const dispatch = useDispatch();
  const { timezone } = useTimezone();
  const [resolvingId, setResolvingId] = useState(null);
  const [feedback, setFeedback] = useState({ id: null, message: "", type: "" });

  const getUsernameFromStorage = () => {
    try {
      const data = localStorage.getItem("currentUser");
      if (!data) return "System_User";
      
      const parsed = JSON.parse(data);
      // Logic to handle both { userName: ... } and { data: { userName: ... } }
      return parsed.userName || parsed.data?.userName || "Unknown_Admin";
    } catch (e) {
      console.error("Failed to parse LocalStorage User", e);
      return "System_User";
    }
  };

  const handleResolve = async (e, incident_id) => {
    e.stopPropagation();
    
    const userName = getUsernameFromStorage();
    setResolvingId(incident_id);
    
    try {
      const response = await itdrApi.post(`/critical-incidents/${incident_id}/resolve`, {
        username: userName
      });

      if (response.data.success) {
        setFeedback({ id: incident_id, message: "Incident Resolved!", type: "success" });

        // Wait 1s so user sees success, then wipe from Redux and refresh
        setTimeout(() => {
          dispatch(removeCriticalIncident(incident_id));
          if (onRefresh) onRefresh();
        }, 1000);
      }
    } catch (error) {
      const errorMsg = error.response?.data?.error?.message || "Resolve failed";
      setFeedback({ id: incident_id, message: errorMsg, type: "error" });
    } finally {
      setResolvingId(null);
      // Clear error message after 3 seconds, but leave success alone (card will vanish)
      setTimeout(() => setFeedback(prev => prev.type === 'error' ? { id: null, message: "", type: "" } : prev), 3000);
    }
  };

  return (
    <div className="adapid-event-stream">
      {events.map((e) => {
        const severityClass = e.severity?.toLowerCase() === "critical" ? "failure" : "warning";
        const isResolving = resolvingId === e.incident_id;
        const isSuccess = feedback.id === e.incident_id && feedback.type === "success";

        return (
          <div key={e.incident_id} className={`adapid-event-card ${severityClass}`}>
            <div className={`adapid-event-indicator ${severityClass}`}>
              <AlertTriangle size={16} style={{ color: severityClass === 'failure' ? '#ff4d4f' : '#faad14' }} />
            </div>

            <div className="adapid-event-content">
              <div className="adapid-event-primary">
                <div className="adapid-event-title">
                  <span className="adapid-actor-name">{e.actor_email}</span>
                </div>
                
                <div className="event-badges">
                  <button 
                    className={`resolve-btn ${isSuccess ? 'success' : ''}`}
                    onClick={(event) => handleResolve(event, e.incident_id)}
                    disabled={isResolving || isSuccess}
                  >
                    {isResolving ? <Loader2 size={12} className="spinner-animate" /> : isSuccess ? <Check size={12} /> : null}
                    {isSuccess ? "Resolved" : isResolving ? "Processing" : "Resolve"}
                  </button>

                  <span className={`status-badge ${severityClass}`}>{e.severity}</span>
                  <span className="adapid-risk-badge high">
                    <Shield size={14} /> Risk: {e.risk_score}
                  </span>
                </div>
              </div>

              <div className="adapid-event-metadata">
                <div className="adapid-metadata-item">
                  <Activity size={14} />
                  <span>Rule: {e.rule_name}</span>
                </div>
                <div className="adapid-metadata-item">
                  <Globe size={14} />
                  <span>IP: {e.ip_address}</span>
                </div>
                <div className="adapid-metadata-item">
                  <Clock size={14} />
                  <span>{formatDateByTimezone(e?.created_at, timezone, "N/A")}</span>
                </div>
              </div>

              {feedback.id === e.incident_id && (
                <div className="adapid-risk-alerts">
                   <div className={`adapid-risk-alert feedback-${feedback.type}`}>
                    <span>{feedback.message}</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};